import React, { useState, useEffect, useMemo } from 'react';
import {
    Card, Row, Col, Select, Table, Tag, Button, Modal, Input, InputNumber,
    Tabs, message, Popconfirm, Space, Empty, Spin, Badge
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import MeService from '../../services/MeService';
import ProgramsService from '../../services/ProgramsService';
import MohReportDataService from '../../services/MohReportDataService';
import { trailCreate, trailUpdate, trailDelete } from '../../utils/activityTrail';

const { Option } = Select;
const { TextArea } = Input;
const FRAMEWORK_CODE = 'MOHPERF';

const FINANCE_STATUS = [
    { value:'GREEN', label:'On Track',   color:'green' },
    { value:'AMBER', label:'Watch',      color:'orange' },
    { value:'RED',   label:'At Risk',    color:'red' },
];

// ── Generic line-item table used by all four tabs ─────────────────────────
// Each tab supplies its own columns, form fields, and service methods; the
// list/add/edit/delete/modal plumbing is identical across all four, so it's
// factored out once here rather than repeated four times.
function LineItemsPanel({ programId, periodLabel, service, entityLabel, columns, formFields, computeDefaults, emptyHint }) {
    const [rows, setRows]       = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm]       = useState({});
    const [saving, setSaving]   = useState(false);

    const load = async () => {
        if (!programId || !periodLabel) { setRows([]); return; }
        setLoading(true);
        try {
            const r = await service.get({ program_id: programId, period_label: periodLabel });
            setRows(r.data?.data || []);
        } catch { message.error(`Failed to load ${entityLabel}`); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [programId, periodLabel]);

    const openAdd = () => { setEditing(null); setForm(computeDefaults ? computeDefaults({}) : {}); setModalOpen(true); };
    const openEdit = (row) => { setEditing(row); setForm({ ...row }); setModalOpen(true); };

    const save = async () => {
        const missing = formFields.filter(f => f.required && !String(form[f.key] ?? '').trim());
        if (missing.length) { message.error(`${missing[0].label} is required`); return; }
        setSaving(true);
        try {
            await service.save({ ...form, program_id: programId, period_label: periodLabel });
            if (editing) trailUpdate('MoH Reporting', `${entityLabel} line`, editing.id);
            else trailCreate('MoH Reporting', `${entityLabel} line`, '');
            message.success(`${entityLabel} entry saved`);
            setModalOpen(false);
            load();
        } catch (e) { message.error(e?.message || `Failed to save ${entityLabel} entry`); }
        finally { setSaving(false); }
    };

    const remove = async (row) => {
        try {
            await service.delete(row.id);
            trailDelete('MoH Reporting', `${entityLabel} line`, row.id);
            message.success('Deleted');
            load();
        } catch { message.error('Failed to delete'); }
    };

    const cols = [
        ...columns,
        { title:'', width:90, render:(_, r) => (
            <Space>
                <Button size="small" icon={<EditOutlined/>} onClick={()=>openEdit(r)}/>
                <Popconfirm title="Delete this entry?" onConfirm={()=>remove(r)}>
                    <Button size="small" danger icon={<DeleteOutlined/>}/>
                </Popconfirm>
            </Space>
        ) },
    ];

    if (!programId || !periodLabel) {
        return <Empty description="Select a submitting unit and reporting period above"/>;
    }

    return (
        <div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
                <div style={{ color:'#888', fontSize:12 }}>{rows.length} {entityLabel.toLowerCase()} entries for this unit &amp; period</div>
                <Button type="primary" icon={<PlusOutlined/>} onClick={openAdd}>Add Entry</Button>
            </div>
            <Table columns={cols} dataSource={rows} rowKey="id" loading={loading} pagination={false} size="small"
                locale={{ emptyText: <Empty description={emptyHint || `No ${entityLabel.toLowerCase()} entries yet`}/> }}/>

            <Modal title={editing ? `Edit ${entityLabel} Entry` : `Add ${entityLabel} Entry`} open={modalOpen}
                onCancel={()=>setModalOpen(false)} onOk={save} confirmLoading={saving} width={560}>
                <Row gutter={12}>
                    {formFields.map(f => (
                        <Col key={f.key} span={f.span || 12} style={{ marginTop:10 }}>
                            <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>
                                {f.label}{f.required && ' *'}
                            </label>
                            {f.type === 'number' ? (
                                <InputNumber style={{width:'100%'}} min={0} value={form[f.key]}
                                    onChange={v => setForm(prev => computeDefaults ? computeDefaults({...prev,[f.key]:v}) : {...prev,[f.key]:v})}/>
                            ) : f.type === 'select' ? (
                                <Select style={{width:'100%'}} value={form[f.key]}
                                    onChange={v => setForm({...form,[f.key]:v})}>
                                    {f.options.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                                </Select>
                            ) : f.type === 'textarea' ? (
                                <TextArea rows={2} value={form[f.key]} onChange={e => setForm({...form,[f.key]:e.target.value})}/>
                            ) : (
                                <Input value={form[f.key]} onChange={e => setForm({...form,[f.key]:e.target.value})}/>
                            )}
                        </Col>
                    ))}
                </Row>
            </Modal>
        </div>
    );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function MoHReportDataEntry() {
    const [frameworkId, setFrameworkId] = useState(null);
    const [periods, setPeriods]         = useState([]);
    const [periodLabel, setPeriodLabel] = useState(null);
    const [units, setUnits]             = useState([]);
    const [programId, setProgramId]     = useState(null);
    const [summary, setSummary]         = useState(null);
    const [loading, setLoading]         = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [fwRes, unitsRes] = await Promise.all([
                    MeService.getFrameworks(),
                    ProgramsService.getPrograms({ status: 1 }),
                ]);
                const fw = (fwRes.data?.data || []).find(f => f.code === FRAMEWORK_CODE);
                setUnits(unitsRes.data?.data || []);
                if (fw) {
                    setFrameworkId(fw.id);
                    const pRes = await MeService.getMePeriods(fw.id);
                    const ps = pRes.data?.data || [];
                    setPeriods(ps);
                    const cur = ps.find(p => p.is_current == 1);
                    setPeriodLabel(cur ? cur.label : (ps[0]?.label || null));
                } else {
                    message.error('MoH Reporting framework not found — run the module migration first');
                }
            } catch { message.error('Failed to load'); }
            finally { setLoading(false); }
        })();
    }, []);

    useEffect(() => {
        if (!programId || !periodLabel) { setSummary(null); return; }
        MohReportDataService.getSummary({ program_id: programId, period_label: periodLabel })
            .then(r => setSummary(r.data?.data || null))
            .catch(() => setSummary(null));
    }, [programId, periodLabel]);

    const selectedUnit = useMemo(() => units.find(u => u.id === programId), [units, programId]);

    if (loading) return <div style={{textAlign:'center',padding:60}}><Spin size="large"/></div>;

    const badge = (n) => n > 0 ? <Badge count={n} style={{ backgroundColor:'#52c41a' }}/> : null;

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{margin:0}}>📋 MoH Reporting — Finance, HR, Clinical &amp; Fleet Data</h4>
                    <small className="text-muted">Tabular datasets that sit alongside the indicator catalog for each unit's quarterly submission</small>
                </div>
            </div>
            <div className="page-pad">
                <Card size="small" style={{ marginBottom:16 }}>
                    <Row gutter={16}>
                        <Col span={10}>
                            <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Submitting Unit</label>
                            <Select showSearch optionFilterProp="children" style={{width:'100%'}}
                                placeholder="Select unit" value={programId} onChange={setProgramId}>
                                {units.map(u => <Option key={u.id} value={u.id}>{u.name} <span style={{color:'#aaa',fontSize:11}}>({u.code})</span></Option>)}
                            </Select>
                        </Col>
                        <Col span={8}>
                            <label style={{fontSize:11,fontWeight:600,display:'block',marginBottom:4}}>Reporting Period</label>
                            <Select style={{width:'100%'}} placeholder="Select period" value={periodLabel} onChange={setPeriodLabel}>
                                {periods.map(p => <Option key={p.label} value={p.label}>{p.is_current==1?'★ ':''}{p.label}</Option>)}
                            </Select>
                        </Col>
                        <Col span={6}>
                            {selectedUnit && <Tag color="blue" style={{marginTop:22}}>{selectedUnit.report_category || selectedUnit.category}</Tag>}
                        </Col>
                    </Row>
                </Card>

                <Tabs
                    defaultActiveKey="finance"
                    items={[
                        {
                            key:'finance', label:<span>Finance {badge(summary?.finance_lines)}</span>,
                            children: (
                                <LineItemsPanel
                                    programId={programId} periodLabel={periodLabel}
                                    service={{ get:MohReportDataService.getFinance, save:MohReportDataService.saveFinance, delete:MohReportDataService.deleteFinance }}
                                    entityLabel="Finance"
                                    emptyHint="No budget lines entered yet for this unit & period"
                                    columns={[
                                        { title:'Budget Line', dataIndex:'description', render:(v,r)=><div><div style={{fontWeight:600}}>{v}</div><div style={{fontSize:11,color:'#888'}}>{r.budget_code}</div></div> },
                                        { title:'Annual Budget', dataIndex:'annual_budget', width:120, render:v=>`E${parseFloat(v||0).toLocaleString()}` },
                                        { title:'Spent/Committed', dataIndex:'cumulative_spent_committed', width:130, render:v=>`E${parseFloat(v||0).toLocaleString()}` },
                                        { title:'Absorption', dataIndex:'absorption_rate_pct', width:100, render:v=>v!=null?`${parseFloat(v).toFixed(1)}%`:'—' },
                                        { title:'Status', dataIndex:'status', width:100, render:s=>{
                                            const meta = FINANCE_STATUS.find(f=>f.value===s);
                                            return meta ? <Tag color={meta.color}>{meta.label}</Tag> : '—';
                                        } },
                                    ]}
                                    formFields={[
                                        { key:'description', label:'Budget Line Description', required:true, span:24 },
                                        { key:'budget_code', label:'Budget Code' },
                                        { key:'status', label:'Status', type:'select', options:FINANCE_STATUS },
                                        { key:'annual_budget', label:'Annual Budget (E)', type:'number' },
                                        { key:'cumulative_released', label:'Cumulative Released (E)', type:'number' },
                                        { key:'cumulative_spent_committed', label:'Spent/Committed (E)', type:'number' },
                                    ]}
                                />
                            ),
                        },
                        {
                            key:'hr', label:<span>HR Establishment {badge(summary?.hr_lines)}</span>,
                            children: (
                                <LineItemsPanel
                                    programId={programId} periodLabel={periodLabel}
                                    service={{ get:MohReportDataService.getHr, save:MohReportDataService.saveHr, delete:MohReportDataService.deleteHr }}
                                    entityLabel="HR"
                                    emptyHint="No cadres entered yet for this unit & period"
                                    columns={[
                                        { title:'Cadre', dataIndex:'cadre', render:(v,r)=><div><div style={{fontWeight:600}}>{v}</div>{r.notes && <div style={{fontSize:11,color:'#888'}}>{r.notes}</div>}</div> },
                                        { title:'Established', dataIndex:'established_posts', width:100 },
                                        { title:'Filled', dataIndex:'filled_posts', width:80 },
                                        { title:'Vacant', dataIndex:'vacant_posts', width:80 },
                                        { title:'Vacancy Rate', dataIndex:'vacancy_rate', width:110, render:v=>v!=null?`${parseFloat(v).toFixed(1)}%`:'—' },
                                    ]}
                                    computeDefaults={(f) => {
                                        const est = parseFloat(f.established_posts||0);
                                        const filled = parseFloat(f.filled_posts||0);
                                        return { ...f, vacant_posts: Math.max(0, est-filled) };
                                    }}
                                    formFields={[
                                        { key:'cadre', label:'Cadre / Post Title', required:true, span:24 },
                                        { key:'established_posts', label:'Established Posts', type:'number' },
                                        { key:'filled_posts', label:'Filled Posts', type:'number' },
                                        { key:'vacant_posts', label:'Vacant Posts (auto, override if needed)', type:'number' },
                                        { key:'notes', label:'Notes', type:'textarea', span:24 },
                                    ]}
                                />
                            ),
                        },
                        {
                            key:'clinical', label:<span>Clinical Services {badge(summary?.clinical_lines)}</span>,
                            children: (
                                <LineItemsPanel
                                    programId={programId} periodLabel={periodLabel}
                                    service={{ get:MohReportDataService.getClinical, save:MohReportDataService.saveClinical, delete:MohReportDataService.deleteClinical }}
                                    entityLabel="Clinical"
                                    emptyHint="No facility service volumes entered yet for this unit & period"
                                    columns={[
                                        { title:'Facility', dataIndex:'facility_name', render:(v,r)=><div><div style={{fontWeight:600}}>{v}</div><div style={{fontSize:11,color:'#888'}}>{r.bed_capacity} beds · catchment {r.catchment_area?.toLocaleString?.()||r.catchment_area}</div></div> },
                                        { title:'OPD Visits', dataIndex:'opd_visits', width:100 },
                                        { title:'Admissions', dataIndex:'admissions', width:100 },
                                        { title:'Deliveries', dataIndex:'deliveries', width:100 },
                                        { title:'Deaths', dataIndex:'deaths', width:80 },
                                    ]}
                                    formFields={[
                                        { key:'facility_name', label:'Facility Name', required:true, span:24 },
                                        { key:'catchment_area', label:'Catchment Population', type:'number' },
                                        { key:'bed_capacity', label:'Bed Capacity', type:'number' },
                                        { key:'opd_visits', label:'OPD Visits', type:'number' },
                                        { key:'admissions', label:'Admissions', type:'number' },
                                        { key:'deliveries', label:'Deliveries', type:'number' },
                                        { key:'inpatient_days', label:'Inpatient Days', type:'number' },
                                        { key:'deaths', label:'Deaths', type:'number' },
                                        { key:'prior_yr_opd_visits', label:'OPD Visits (Same Period, Prior Year)', type:'number', span:24 },
                                    ]}
                                />
                            ),
                        },
                        {
                            key:'fleet', label:<span>Fleet {badge(summary?.fleet_lines)}</span>,
                            children: (
                                <LineItemsPanel
                                    programId={programId} periodLabel={periodLabel}
                                    service={{ get:MohReportDataService.getFleet, save:MohReportDataService.saveFleet, delete:MohReportDataService.deleteFleet }}
                                    entityLabel="Fleet"
                                    emptyHint="No vehicle categories entered yet for this unit & period"
                                    columns={[
                                        { title:'Vehicle Category', dataIndex:'vehicle_category', render:(v,r)=><div><div style={{fontWeight:600}}>{v}</div>{r.comments && <div style={{fontSize:11,color:'#888'}}>{r.comments}</div>}</div> },
                                        { title:'Total', dataIndex:'total_vehicles', width:80 },
                                        { title:'Active', dataIndex:'active_running', width:90, render:v=><Tag color="green">{v}</Tag> },
                                        { title:'Grounded', dataIndex:'grounded_nonrunner', width:100, render:v=>parseInt(v)>0?<Tag color="red">{v}</Tag>:v },
                                    ]}
                                    formFields={[
                                        { key:'vehicle_category', label:'Vehicle Category', required:true, span:24 },
                                        { key:'total_vehicles', label:'Total Vehicles', type:'number' },
                                        { key:'govt_bought', label:'Government-Bought', type:'number' },
                                        { key:'donated_partner', label:'Donated / Partner', type:'number' },
                                        { key:'active_running', label:'Active / Running', type:'number' },
                                        { key:'grounded_nonrunner', label:'Grounded / Non-Runner', type:'number' },
                                        { key:'comments', label:'Comments', type:'textarea', span:24 },
                                    ]}
                                />
                            ),
                        },
                    ]}
                />
            </div>
        </div>
    );
}